/**
 * Subscription Products Tab
 * Manage subscription products (monthly, yearly, lifetime)
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, RefreshCw, Package, Edit2, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { toast } from 'sonner';
import clsx from 'clsx';
import { useI18n } from '@/i18n';
import { baseApiClient } from '@/lib/api/client';

// Types
interface Product {
  id: string;
  name: string;
  description: string | null;
  display_price: string | null;
  price_cents: number | null;
  currency: string | null;
  apple_product_id: string | null;
  google_product_id: string | null;
  is_available: boolean;
  sort_order: number;
  extra_data: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

interface ProductListResponse {
  products: Product[];
}

interface PlatformConfig {
  payment_type: string; // "one_time" | "recurring"
  badge: string;
  subtitle: string;
  subject: string; // Payment page title (e.g. "算了Pro会员 · 1个月")
}

interface UpdateProductRequest {
  name?: string;
  description?: string;
  display_price?: string;
  price_cents?: number;
  currency?: string;
  apple_product_id?: string;
  google_product_id?: string;
  is_available?: boolean;
  sort_order?: number;
  extra_data?: Record<string, unknown>;
}

// API functions
async function fetchProducts(includeUnavailable: boolean = true): Promise<ProductListResponse> {
  const { data } = await baseApiClient.get<ProductListResponse>(
    `/v1/subscription/admin/products?include_unavailable=${includeUnavailable}`
  );
  return data;
}

async function updateProduct(productId: string, payload: UpdateProductRequest): Promise<Product> {
  const { data } = await baseApiClient.put<Product>(
    `/v1/subscription/admin/products/${productId}`,
    payload
  );
  return data;
}

// Product Card Component
function ProductCard({
  product,
  onEdit,
  onToggleAvailability,
  t,
}: {
  product: Product;
  onEdit: () => void;
  onToggleAvailability: () => void;
  t: (key: string) => string;
}) {
  return (
    <div className={clsx(
      'bg-white rounded-lg border p-6 transition-all',
      product.is_available ? 'border-gray-200' : 'border-gray-200 opacity-60'
    )}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={clsx(
            'p-2 rounded-lg',
            product.is_available ? 'bg-primary-50 text-primary-600' : 'bg-gray-100 text-gray-400'
          )}>
            <Package className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{product.name}</h3>
            <p className="text-sm text-gray-500">{product.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onToggleAvailability}
            className={clsx(
              'p-2 rounded-lg transition-colors',
              product.is_available
                ? 'text-green-600 hover:bg-green-50'
                : 'text-gray-400 hover:bg-gray-100'
            )}
            title={product.is_available ? t('subscriptionPage.products.disableProduct') : t('subscriptionPage.products.enableProduct')}
          >
            {product.is_available ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
          <button
            onClick={onEdit}
            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {product.description && (
        <p className="text-sm text-gray-600 mb-4">{product.description}</p>
      )}

      <div className="space-y-2">
        {product.display_price && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{t('subscriptionPage.products.displayPrice')}</span>
            <span className="font-medium text-gray-900">{product.display_price}</span>
          </div>
        )}
        {product.price_cents != null && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{t('subscriptionPage.products.priceYuan')}</span>
            <span className="font-medium text-orange-600">¥{(product.price_cents / 100).toFixed(2)}</span>
          </div>
        )}
        {product.apple_product_id && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{t('subscriptionPage.products.appleProductId')}</span>
            <span className="font-mono text-xs text-gray-700">{product.apple_product_id}</span>
          </div>
        )}
        {product.google_product_id && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{t('subscriptionPage.products.googleProductId')}</span>
            <span className="font-mono text-xs text-gray-700">{product.google_product_id}</span>
          </div>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between text-xs text-gray-400">
        <span>{t('subscriptionPage.products.sortOrder')}: {product.sort_order}</span>
        <span>
          {product.is_available ? (
            <span className="text-green-600 font-medium">{t('subscriptionPage.products.available')}</span>
          ) : (
            <span className="text-gray-400">{t('subscriptionPage.products.unavailable')}</span>
          )}
        </span>
      </div>
    </div>
  );
}

// Edit Modal Component
function EditProductModal({
  product,
  onClose,
  onSave,
  isLoading,
  t,
}: {
  product: Product;
  onClose: () => void;
  onSave: (data: UpdateProductRequest) => void;
  isLoading: boolean;
  t: (key: string) => string;
}) {
  // Parse existing extra_data for platform config
  const existingExtra = (product.extra_data || {}) as Record<string, unknown>;
  const existingPlatforms = (existingExtra.platforms || {}) as Record<string, PlatformConfig>;
  const existingIos = existingPlatforms.ios || { payment_type: 'recurring', badge: '自动续费', subtitle: '连续包月，可随时取消' };
  const existingAndroid = existingPlatforms.android || { payment_type: 'one_time', badge: '单次购买', subtitle: '购买后有效期30天' };

  const [form, setForm] = useState({
    name: product.name,
    description: product.description || '',
    display_price: product.display_price || '',
    price_yuan: product.price_cents != null ? (product.price_cents / 100).toFixed(2) : '',
    currency: product.currency || 'CNY',
    apple_product_id: product.apple_product_id || '',
    google_product_id: product.google_product_id || '',
    sort_order: product.sort_order,
    // Platform config
    period_label: (existingExtra.period_label as string) || '',
    period_days: (existingExtra.period_days as number) || 30,
    ios_payment_type: existingIos.payment_type || 'recurring',
    ios_badge: existingIos.badge || '',
    ios_subtitle: existingIos.subtitle || '',
    ios_subject: existingIos.subject || '',
    android_payment_type: existingAndroid.payment_type || 'one_time',
    android_badge: existingAndroid.badge || '',
    android_subtitle: existingAndroid.subtitle || '',
    android_subject: existingAndroid.subject || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseFloat(form.price_yuan);
    const priceCents = form.price_yuan && !isNaN(parsed) ? Math.round(parsed * 100) : undefined;
    onSave({
      name: form.name,
      description: form.description || undefined,
      display_price: form.display_price || undefined,
      price_cents: priceCents,
      currency: form.currency || undefined,
      apple_product_id: form.apple_product_id || undefined,
      google_product_id: form.google_product_id || undefined,
      sort_order: form.sort_order,
      extra_data: {
        ...existingExtra,
        period_label: form.period_label || undefined,
        period_days: form.period_days || undefined,
        platforms: {
          ...existingPlatforms, // preserve unknown platform keys (e.g. huawei)
          ios: {
            payment_type: form.ios_payment_type,
            badge: form.ios_badge,
            subtitle: form.ios_subtitle,
            subject: form.ios_subject,
          },
          android: {
            payment_type: form.android_payment_type,
            badge: form.android_badge,
            subtitle: form.android_subtitle,
            subject: form.android_subject,
          },
        },
      },
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">{t('subscriptionPage.products.editProduct')}: {product.id}</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.products.name')}</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.products.description')}</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Alipay (Android) Section */}
          <div className="border border-orange-200 rounded-lg overflow-hidden">
            <div className="bg-orange-50 px-4 py-2 border-b border-orange-200">
              <h4 className="text-sm font-medium text-orange-800">{t('subscriptionPage.products.alipaySection')}</h4>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.products.priceYuan')}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-400">¥</span>
                    <input
                      type="number"
                      step="0.01"
                      value={form.price_yuan}
                      onChange={(e) => setForm(f => ({ ...f, price_yuan: e.target.value }))}
                      placeholder="68.00"
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.products.currency')}</label>
                  <select
                    value={form.currency}
                    onChange={(e) => setForm(f => ({ ...f, currency: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="CNY">CNY (¥)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.products.displayPrice')}</label>
                <input
                  type="text"
                  value={form.display_price}
                  onChange={(e) => setForm(f => ({ ...f, display_price: e.target.value }))}
                  placeholder="¥68/月"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <p className="text-xs text-orange-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {t('subscriptionPage.products.alipayHint')}
              </p>
            </div>
          </div>

          {/* Apple IAP (iOS) Section */}
          <div className="border border-blue-200 rounded-lg overflow-hidden">
            <div className="bg-blue-50 px-4 py-2 border-b border-blue-200">
              <h4 className="text-sm font-medium text-blue-800">{t('subscriptionPage.products.appleSection')}</h4>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.products.appleProductId')}</label>
                <input
                  type="text"
                  value={form.apple_product_id}
                  onChange={(e) => setForm(f => ({ ...f, apple_product_id: e.target.value }))}
                  placeholder="com.doratech.dora.pro.monthly"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                />
              </div>
              <p className="text-xs text-blue-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {t('subscriptionPage.products.appleHint')}
              </p>
            </div>
          </div>

          {/* Platform Config Section */}
          <div className="border border-purple-200 rounded-lg overflow-hidden">
            <div className="bg-purple-50 px-4 py-2 border-b border-purple-200">
              <h4 className="text-sm font-medium text-purple-800">平台配置</h4>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">周期标签</label>
                  <input
                    type="text"
                    value={form.period_label}
                    onChange={(e) => setForm(f => ({ ...f, period_label: e.target.value }))}
                    placeholder="1个月 / 3个月 / 1年"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">有效期天数</label>
                  <input
                    type="number"
                    value={form.period_days}
                    onChange={(e) => setForm(f => ({ ...f, period_days: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm"
                  />
                </div>
              </div>

              {/* iOS */}
              <div className="bg-blue-50/50 rounded-md p-3 space-y-2">
                <p className="text-xs font-medium text-blue-700">iOS (Apple IAP)</p>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">付费类型</label>
                    <select
                      value={form.ios_payment_type}
                      onChange={(e) => setForm(f => ({ ...f, ios_payment_type: e.target.value }))}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    >
                      <option value="recurring">自动续费</option>
                      <option value="one_time">单次购买</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">标签</label>
                    <input
                      type="text"
                      value={form.ios_badge}
                      onChange={(e) => setForm(f => ({ ...f, ios_badge: e.target.value }))}
                      placeholder="自动续费"
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">副标题</label>
                    <input
                      type="text"
                      value={form.ios_subtitle}
                      onChange={(e) => setForm(f => ({ ...f, ios_subtitle: e.target.value }))}
                      placeholder="连续包月，可随时取消"
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">支付页标题</label>
                  <input
                    type="text"
                    value={form.ios_subject}
                    onChange={(e) => setForm(f => ({ ...f, ios_subject: e.target.value }))}
                    placeholder="月度会员"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>

              {/* Android */}
              <div className="bg-orange-50/50 rounded-md p-3 space-y-2">
                <p className="text-xs font-medium text-orange-700">Android (支付宝)</p>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">付费类型</label>
                    <select
                      value={form.android_payment_type}
                      onChange={(e) => setForm(f => ({ ...f, android_payment_type: e.target.value }))}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    >
                      <option value="one_time">单次购买</option>
                      <option value="recurring">自动续费</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">标签</label>
                    <input
                      type="text"
                      value={form.android_badge}
                      onChange={(e) => setForm(f => ({ ...f, android_badge: e.target.value }))}
                      placeholder="单次购买"
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">副标题</label>
                    <input
                      type="text"
                      value={form.android_subtitle}
                      onChange={(e) => setForm(f => ({ ...f, android_subtitle: e.target.value }))}
                      placeholder="购买后有效期30天"
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">支付页标题 (支付宝显示)</label>
                  <input
                    type="text"
                    value={form.android_subject}
                    onChange={(e) => setForm(f => ({ ...f, android_subject: e.target.value }))}
                    placeholder="算了Pro会员 · 1个月"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.products.sortOrder')}</label>
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.products.googleProductId')}</label>
              <input
                type="text"
                value={form.google_product_id}
                onChange={(e) => setForm(f => ({ ...f, google_product_id: e.target.value }))}
                placeholder="monthly_subscription"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('subscriptionPage.products.cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? t('subscriptionPage.products.saving') : t('subscriptionPage.products.saveChanges')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function SubscriptionProductsTab() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['subscription-products'],
    queryFn: () => fetchProducts(true),
  });

  const updateMutation = useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: UpdateProductRequest }) =>
      updateProduct(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-products'] });
      toast.success(t('subscriptionPage.products.savedSuccess'));
      setEditingProduct(null);
    },
    onError: (err: Error) => {
      toast.error(`${t('subscriptionPage.products.saveFailed')}: ${err.message}`);
    },
  });

  const handleToggleAvailability = (product: Product) => {
    updateMutation.mutate({
      productId: product.id,
      data: { is_available: !product.is_available },
    });
  };

  const handleSave = (data: UpdateProductRequest) => {
    if (!editingProduct) return;
    updateMutation.mutate({
      productId: editingProduct.id,
      data,
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('subscriptionPage.products.title')}</h2>
          <p className="text-sm text-gray-500">{t('subscriptionPage.products.subtitle')}</p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('subscriptionPage.products.refresh')}
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div>
            <h3 className="text-red-800 font-semibold">{t('subscriptionPage.products.errorLoading')}</h3>
            <p className="text-red-600 text-sm">{(error as Error).message}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      )}

      {/* Products Grid */}
      {data && (
        <>
          {data.products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-gray-600 font-medium">{t('subscriptionPage.products.noProducts')}</h3>
              <p className="text-gray-400 text-sm mt-1">
                {t('subscriptionPage.products.noProductsHint')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-6">
              {data.products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onEdit={() => setEditingProduct(product)}
                  onToggleAvailability={() => handleToggleAvailability(product)}
                  t={t}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Edit Modal */}
      {editingProduct && (
        <EditProductModal
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          onSave={handleSave}
          isLoading={updateMutation.isPending}
          t={t}
        />
      )}
    </div>
  );
}
